import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@apex/shared";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "secondary" | "outline" | "ghost" | "destructive" | "cta";
  size?: "sm" | "md" | "lg";
  isLoading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "default",
      size = "md",
      isLoading = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const baseStyles =
      "apex-inline-flex apex-items-center apex-justify-center apex-font-medium apex-rounded-md apex-transition-colors focus-visible:apex-outline-none focus-visible:apex-ring-2 focus-visible:apex-ring-ring focus-visible:apex-ring-offset-2 disabled:apex-opacity-50 disabled:apex-pointer-events-none";

    const variants = {
      default:
        "apex-bg-primary apex-text-primary-foreground hover:apex-bg-primary/90",
      secondary:
        "apex-bg-secondary apex-text-secondary-foreground hover:apex-bg-secondary/80",
      outline:
        "apex-border apex-border-input apex-bg-background hover:apex-bg-accent hover:apex-text-accent-foreground",
      ghost: "hover:apex-bg-accent hover:apex-text-accent-foreground",
      destructive:
        "apex-bg-destructive apex-text-destructive-foreground hover:apex-bg-destructive/90",
      cta:
        "apex-bg-accent-cta apex-text-accent-cta-foreground hover:apex-bg-accent-cta-hover apex-cta-glow apex-font-semibold",
    };

    const sizes = {
      sm: "apex-h-8 apex-px-3 apex-text-sm",
      md: "apex-h-10 apex-px-4 apex-text-sm",
      lg: "apex-h-12 apex-px-6 apex-text-base",
    };

    return (
      <button
        ref={ref}
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <>
            <svg
              className="apex-animate-spin apex--ml-1 apex-mr-2 apex-h-4 apex-w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="apex-opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="apex-opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Loading...
          </>
        ) : (
          children
        )}
      </button>
    );
  }
);

Button.displayName = "Button";
